import {
  Box,
  Text,
  Group,
  Avatar,
  ActionIcon,
  Menu,
  useMantineTheme,
  Tooltip,
} from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import {
  IconDots,
  IconCopy,
  IconTrash,
  IconPencil,
  IconCornerUpLeft,
  IconCheck,
  IconChecks,
} from '@tabler/icons-react';
import { getContrastingTextColor } from '../../../utils/color';

export interface ChatBubbleProps {
  message: string;
  sender: {
    name: string;
    avatar?: string;
  };
  timestamp: Date | string;
  isOwn?: boolean;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'error';
  replyTo?: {
    sender: string;
    message: string;
  };
  attachments?: {
    type: 'image' | 'file' | 'video';
    url: string;
    name?: string;
    thumbnail?: string;
  }[];
  reactions?: {
    emoji: string;
    count: number;
    reacted?: boolean;
  }[];
  isEdited?: boolean;
  onReply?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  onReact?: (emoji: string) => void;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  showAvatar?: boolean;
  showTimestamp?: boolean;
  grouped?: boolean;
}

const formatTime = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatBubble = forwardRef<HTMLDivElement, ChatBubbleProps>(
  (
    {
      message,
      sender,
      timestamp,
      isOwn = false,
      status,
      replyTo,
      attachments,
      reactions,
      isEdited,
      onReply,
      onEdit,
      onDelete,
      onCopy,
      onReact,
      gradientVariant = 'primary',
      showAvatar = true,
      showTimestamp = true,
      grouped = false,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];

    const gradientBg = `linear-gradient(135deg, ${gradient?.from}, ${gradient?.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const getStatusIcon = () => {
      switch (status) {
        case 'sent':
          return <IconCheck size={12} />;
        case 'delivered':
          return <IconChecks size={12} />;
        case 'read':
          return <IconChecks size={12} color={gradient?.from} />;
        default:
          return null;
      }
    };

    return (
      <Box
        ref={ref}
        style={{
          display: 'flex',
          flexDirection: isOwn ? 'row-reverse' : 'row',
          gap: theme.spacing.xs,
          marginBottom: grouped ? 2 : theme.spacing.sm,
        }}
      >
        {/* Avatar */}
        {showAvatar && !grouped && (
          <Avatar
            src={sender.avatar}
            alt={sender.name}
            size="sm"
            radius="xl"
            style={{ visibility: isOwn ? 'hidden' : 'visible' }}
          >
            {sender.name.charAt(0).toUpperCase()}
          </Avatar>
        )}
        {showAvatar && grouped && <Box w={30} />}

        {/* Message bubble */}
        <Box style={{ maxWidth: '70%' }}>
          {/* Sender name (for non-own messages and not grouped) */}
          {!isOwn && !grouped && (
            <Text size="xs" fw={600} mb={2} ml={4}>
              {sender.name}
            </Text>
          )}

          {/* Reply preview */}
          {replyTo && (
            <Box
              mb={4}
              p="xs"
              style={{
                backgroundColor: theme.colors.gray[1],
                borderRadius: theme.radius.sm,
                borderLeft: `3px solid ${gradient?.from}`,
              }}
            >
              <Text size="xs" fw={600} c={gradient?.from}>
                {replyTo.sender}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {replyTo.message}
              </Text>
            </Box>
          )}

          {/* Main bubble */}
          <Box
            p="sm"
            style={{
              backgroundColor: isOwn ? gradient?.from : theme.colors.gray[1],
              color: isOwn ? autoTextColor : undefined,
              borderRadius: theme.radius.lg,
              borderTopLeftRadius: isOwn ? theme.radius.lg : grouped ? theme.radius.sm : theme.radius.lg,
              borderTopRightRadius: isOwn ? (grouped ? theme.radius.sm : theme.radius.lg) : theme.radius.lg,
              position: 'relative',
            }}
          >
            {/* Message text */}
            <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message}
            </Text>

            {/* Attachments */}
            {attachments && attachments.length > 0 && (
              <Box mt="xs">
                {attachments.map((attachment, index) => (
                  <Box key={index} mb="xs">
                    {attachment.type === 'image' && (
                      <Box
                        component="img"
                        src={attachment.thumbnail || attachment.url}
                        alt={attachment.name}
                        style={{
                          maxWidth: '100%',
                          borderRadius: theme.radius.sm,
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    {attachment.type === 'file' && (
                      <Group
                        gap="xs"
                        p="xs"
                        style={{
                          backgroundColor: isOwn ? 'rgba(255,255,255,0.1)' : theme.colors.gray[2],
                          borderRadius: theme.radius.sm,
                          color: isOwn ? autoTextColor : undefined,
                        }}
                      >
                        <Text size="sm">{attachment.name}</Text>
                      </Group>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Timestamp and status */}
            <Group gap={4} justify="flex-end" mt={4}>
              {isEdited && (
                <Text size="xs" c={isOwn ? autoTextColor : 'dimmed'} style={isOwn ? { opacity: 0.7 } : undefined}>
                  edited
                </Text>
              )}
              {showTimestamp && (
                <Text size="xs" c={isOwn ? autoTextColor : 'dimmed'} style={isOwn ? { opacity: 0.7 } : undefined}>
                  {formatTime(timestamp)}
                </Text>
              )}
              {isOwn && status && getStatusIcon()}
            </Group>

            {/* Context menu trigger */}
            <Menu shadow="md" width={150} position={isOwn ? 'left-start' : 'right-start'}>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  pos="absolute"
                  top={4}
                  right={isOwn ? undefined : 4}
                  left={isOwn ? 4 : undefined}
                  style={{ opacity: 0.5 }}
                  c={isOwn ? autoTextColor : undefined}
                >
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {onReply && (
                  <Menu.Item leftSection={<IconCornerUpLeft size={14} />} onClick={onReply}>
                    Reply
                  </Menu.Item>
                )}
                {onCopy && (
                  <Menu.Item leftSection={<IconCopy size={14} />} onClick={onCopy}>
                    Copy
                  </Menu.Item>
                )}
                {isOwn && onEdit && (
                  <Menu.Item leftSection={<IconPencil size={14} />} onClick={onEdit}>
                    Edit
                  </Menu.Item>
                )}
                {isOwn && onDelete && (
                  <>
                    <Menu.Divider />
                    <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
                      Delete
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </Box>

          {/* Reactions */}
          {reactions && reactions.length > 0 && (
            <Group gap={4} mt={4} justify={isOwn ? 'flex-end' : 'flex-start'}>
              {reactions.map((reaction, index) => (
                <Tooltip key={index} label={`${reaction.count} reactions`}>
                  <Box
                    px={6}
                    py={2}
                    style={{
                      backgroundColor: reaction.reacted ? `${gradient?.from}20` : theme.colors.gray[1],
                      borderRadius: theme.radius.xl,
                      cursor: 'pointer',
                      border: reaction.reacted ? `1px solid ${gradient?.from}` : '1px solid transparent',
                    }}
                    onClick={() => onReact?.(reaction.emoji)}
                  >
                    <Group gap={4}>
                      <Text size="xs">{reaction.emoji}</Text>
                      <Text size="xs" fw={500}>
                        {reaction.count}
                      </Text>
                    </Group>
                  </Box>
                </Tooltip>
              ))}
            </Group>
          )}
        </Box>
      </Box>
    );
  }
);

ChatBubble.displayName = 'ChatBubble';

export default ChatBubble;
